import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { clone } from "@/util/clone";
import { sortStructure } from "@/util/sortStructure.js";

export const useCulvertStore = defineStore("culvert", () => {
  //culvert data is loaded either from idb (pouch db for inspection module) or service (for management module)
  let culvertData = ref({});
  let culvertDataOriginal = ref({});

  const loadCulvertObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      //if moduleType is management, load data from service call
      await getInspLinkCulvert(brkey, inspectionId);
    }
  };

  const getCulvertObject = () => {
    return culvertData.value;
  };

  const resetPageData = () => {
    culvertData.value = clone(culvertDataOriginal.value);
  };

  const getStructureUnits = (structure) => {
    const sortedStructures = sortStructure(structure, "structureUnits", "strUnitLabel");
    return sortedStructures?.filter(
      (structureUnit) =>
        structureUnit.spanConfigDesignation?.startsWith("C") || structureUnit.spanConfigDesignation?.startsWith("V"),
    );
  };

  const getCulvertWaterwayUnits = (structure) => {
    return structure?.structureUnits?.filter(
      (structureUnit) => structureUnit.legacyStructureType == "C" && structureUnit.within500YRFloodPlain == "1",
    );
  };

  const getTotalNumberOfSpans = (structure) => {
    return structure?.structureUnitSpanSets?.reduce((total, spanSet) => {
      if ("C" == spanSet.spanConfigDesignation?.[0]) {
        return total + spanSet?.numberOfSpans || 0;
      }
      return total;
    }, 0);
  };

  const saveCulvert = async (moduleType, culvertValue, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(culvertValue, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateCulvert(culvertValue);
    }
    return isSuccess;
  };

  const updateCulvert = async (culvert) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_CULVERT;
    await axios
      .put(url, culvert)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Culvert", response);
        culvertData.value = { ...response.data.data };
        culvertDataOriginal.value = clone(culvertData.value);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspModuleObject = (culvertValue, structure) => {
    structure.inspection.culvRating = culvertValue.culvRating;
    structure.userInspection.matStoreUnd = culvertValue.matStoreUnd;
    structure.userInspection.newPaint = culvertValue.newPaint;
    structure.userInspection.paintCondRate = culvertValue.paintCondRate;
    structure.userInspection.paintExtentRate = culvertValue.paintExtentRate;
    structure.structureBridge.mainSpans = culvertValue.mainSpans;
    structure.structureBridge.appSpans = culvertValue.appSpans;
    structure.structureBridge.nbisBridgeLength = culvertValue.nbisBridgeLength;
    structure.structureUnitInspComments = culvertValue.structureUnitInspComments;
    structure.inspectionsUnderwater = culvertValue.inspectionsUnderwater;
  };

  const convertToInspModuleObject = (structure) => {
    culvertData.value = {
      brkey: structure.structureBridge.brkey,
      inspectionId: structure.inspection.inspectionId,
      culvRating: structure.inspection.culvRating,
      matStoreUnd: structure.userInspection.matStoreUnd,
      newPaint: structure.userInspection.newPaint,
      paintCondRate: structure.userInspection.paintCondRate,
      paintExtentRate: structure.userInspection.paintExtentRate,
      mainSpans: structure.structureBridge.mainSpans,
      appSpans: structure.structureBridge.appSpans,
      culvertOpenings: structure.culvertOpenings,
      nbisBridgeLength: structure.structureBridge.nbisBridgeLength,
      skew: structure.structureBridge.skew,
      culvRatingDuration: structure.inspectionConditionHist?.culvRatingDuration,
      culvRatingDurationChanges: structure.inspectionConditionHist?.culvRatingDurationChanges,
      paintCondRateDuration: structure.inspectionConditionHist?.paintCondRateDuration,
      paintCondRateDurationChanges: structure.inspectionConditionHist?.paintCondRateDurationChanges,
      paintExtentRateDuration: structure.inspectionConditionHist?.paintExtentRateDuration,
      paintExtentRateDurationChanges: structure.inspectionConditionHist?.paintExtentRateDurationChanges,
      structureUnitInspComments: structure.structureUnitInspComments,
      structureUnits: getStructureUnits(structure),
      structureUnitSpanSets: structure.structureUnitSpanSets,
      inspectionsUnderwater: structure.inspectionsUnderwater,
      totalNumberOfSpans: getTotalNumberOfSpans(structure),
    };
    culvertDataOriginal.value = clone(culvertData.value);
  };

  const getInspLinkCulvert = async (brkey, inspectionId) => {
    culvertData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_CULVERT.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);
    await axios
      .get(url)
      .then((response) => {
        const sortedStructures = sortStructure(response.data.data, "structureUnits", "strUnitLabel");
        culvertData.value = { ...response.data.data, structureUnits: sortedStructures };
        culvertDataOriginal.value = clone(culvertData.value);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  return {
    getStructureUnits,
    resetPageData,
    culvertData,
    getCulvertObject,
    saveCulvert,
    loadCulvertObject,
    getCulvertWaterwayUnits,
    getTotalNumberOfSpans,
  };
});
